/**
 * What a format is, stated once for every layer that holds formatted text.
 *
 * A format is not a document. A task's description, a chat turn and a doc in
 * the docs store are all text written in one, and only the last is a record
 * with an identity and a history. So a format answers for the TEXT alone — its
 * outline, the text a search reads, what its editor offers — and knows nothing
 * about where the text is kept or who may read it.
 *
 * **The package is pure: no database, no DOM, no React, no Node built-in.** It
 * runs the same in the browser and on the server, which is the whole of what
 * pure means here. A library BOTH sides can run is welcome, and markdown's
 * parser is one. What stays out is a library only one side can run — a
 * sanitizer the browser cannot load, an editor the server cannot — because
 * Bun's isolated linker hoists nothing between workspaces, so naming one hands
 * it to the side that cannot use it. Those belong to an adapter in the app
 * that owns them.
 *
 * **A limit belongs to the field, not the format.** How long a body may be
 * follows from what the field costs to return and to store, which differs
 * between a description sent inline with every listing and a doc fetched on
 * its own. Each field states its own limit where it is defined.
 */

/** One entry of an outline, and the entries nested under it. */
export type OutlineEntry = {
  /** The anchor the rendered heading answers to. */
  id: string
  label: string
  children?: OutlineEntry[]
}

/**
 * The scope of the ONE document a page is about.
 *
 * Every heading id is made inside a scope, this one included, so no heading
 * can be given an id the app itself uses: a doc with a heading called "Root"
 * would otherwise answer to the id the app mounts on. It holds the mark that
 * joins a scope to a slug, which no id written by hand does.
 */
export const PAGE_SCOPE = 'doc'

/** A stretch of source text: the selection, or the caret when both are equal. */
export type Selection = { start: number; end: number }

/**
 * An edit to SOURCE TEXT, stated as what it does to the selection.
 *
 * `wrap` surrounds the selection; `prefix` opens every line the selection
 * touches. Both describe a format a person writes as its own source, marks and
 * all, and nothing else: a format edited any other way has no use for either,
 * and offers no tools of this kind.
 */
export type SourceCommand =
  | { kind: 'wrap'; before: string; after: string }
  | { kind: 'prefix'; prefix: string }

/** One thing the editor of a format written as its source offers. */
export type FormatTool = {
  id: string
  /** What the tool is called, for its tooltip and its accessible name. */
  title: string
  /** The few characters drawn on the control. */
  mark: string
  command: SourceCommand
}

export type Format = {
  /** Where a document of this format keeps its authored content. */
  bodyStorage: 'inline' | 'rows'
  /** Whether a body is valid source for this format. */
  validate: (body: string) => boolean
  /**
   * The headings of a body as a contents nests them, for the one document a
   * page is about: every id is made inside `PAGE_SCOPE`.
   */
  outline: (body: string) => OutlineEntry[]
  /** What a search is matched against, and what an excerpt is cut from. */
  searchText: (body: string) => string
  /** What a source editor offers, in the order it offers them. */
  tools: readonly FormatTool[]
}

/**
 * The source after a command, where the caret rests, and the one stretch of
 * the source the command replaced — so an editor can make the edit as one
 * insertion of its own, which the field's undo then takes back whole.
 */
export type Edited = {
  value: string
  caret: number
  replaced: Selection & { text: string }
}

/**
 * Apply a command to the source.
 *
 * Pure, so what a tool does is decided here and an editor only carries it out:
 * it reads the selection off its own control, and puts the caret where this
 * says.
 */
export function applyCommand(
  value: string,
  selection: Selection,
  command: SourceCommand,
): Edited {
  const { start, end } = selection
  if (command.kind === 'wrap') {
    const text = command.before + value.slice(start, end) + command.after
    return {
      value: value.slice(0, start) + text + value.slice(end),
      caret: start + text.length,
      replaced: { start, end, text },
    }
  }

  /* A line is prefixed whole, so the edit begins at the start of the line the
     selection opens on rather than at the selection. */
  const lineStart = value.lastIndexOf('\n', start - 1) + 1
  const text = value
    .slice(lineStart, end)
    .split('\n')
    .map((line) => command.prefix + line)
    .join('\n')
  return {
    value: value.slice(0, lineStart) + text + value.slice(end),
    caret: lineStart + text.length,
    replaced: { start: lineStart, end, text },
  }
}
